import { Fragment, useMemo, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { formatBoxTruckDimensions, formatBoxTruckDoor, type BoxTruckDriverAssignmentsDto, type BoxTruckMyDriverCompanyDepositDto } from '@numeo/types-core'
import { Alert, SearchField, ToggleButtonGroup } from '@/components/ds2'
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard'
import { trackBoxTruckUiInteraction } from '../utils/analytics'
import { BoxTruckVerification } from '../services/boxTruckLoadsApi'
import { myDriverCompanyDepositGroups, type QuickViewField } from './myDriverCompanyDepositFields'

/** One copyable value: click anywhere on the field to put the value on the clipboard (dispatchers live by
 *  copy-paste — no select-drag, no context menu). Empty values render muted and aren't clickable. The
 *  shared hook owns the confirmation timer (cleared on unmount) and surfaces a refused clipboard as an
 *  error instead of a false checkmark. */
const CopyField = ({ label, value }: { label: string; value?: string | number | null }) => {
    const { copiedKey: copied, copy } = useCopyToClipboard()
    const text = value == null || value === '' ? '' : String(value)
    return (
        <button
            type="button"
            onClick={() => void copy(text || undefined)}
            disabled={!text}
            title={text ? 'Click to copy' : undefined}
            className={`flex min-w-0 flex-col items-start rounded-xl border px-3 py-2 text-left transition-colors ${
                text
                    ? 'cursor-copy border-border bg-background hover:border-accent/50 hover:bg-accent-soft/40'
                    : 'cursor-default border-border/60 bg-background/50'
            }`}
        >
            <span className="text-[11px] font-medium uppercase tracking-wider text-foreground-muted">{label}</span>
            <span className="mt-0.5 flex w-full min-w-0 items-center justify-between gap-2 text-[15px] font-medium leading-snug text-foreground">
                <span className="truncate">{text || '—'}</span>
                {text && (copied ? <Check size={15} className="shrink-0 text-success" /> : <Copy size={15} className="shrink-0 text-foreground-muted/60" />)}
            </span>
        </button>
    )
}

/**
 * One field, as DATA.
 *
 * The panel used to be JSX all the way down, which is why it could not be searched: there was nothing to
 * filter. Declaring the fields and rendering them afterwards is what makes "find me the VIN" a one-line
 * filter instead of a restructure.
 */
/** Related values in one tile. Empty when every part is empty, so the field still shows as unanswered. */
const join = (separator: string, ...parts: (string | number | null | undefined)[]) => {
    const kept = parts.filter((part) => part != null && part !== '').map(String)
    return kept.length > 0 ? kept.join(separator) : undefined
}

/**
 * One titled group of dense fields — the atrek "Update Status" panel's 3-column read grid.
 *
 * `showTitle` is false when the filter chip above is the only group on screen: it already names it, and a
 * heading repeating the selected chip is the kind of duplication this panel exists to remove.
 */
const Group = ({ title, fields, showTitle = true }: { title: string; fields: QuickViewField[]; showTitle?: boolean }) => (
    <div>
        {showTitle && (
            <div className="mb-2 flex items-center gap-2.5">
                <span className="text-sm font-semibold text-foreground">{title}</span>
                <span className="h-px flex-1 bg-border" />
            </div>
        )}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {fields.map((field) => (
                <CopyField key={field.label} label={field.label} value={field.value} />
            ))}
        </div>
    </div>
)

/**
 * Read-first driver summary with one copyable field grid, search and group filter. My Drivers adds current
 * company and deposit groups after its scoped read; cross-tenant Verifications receives neither.
 * Vehicle fields come from the claim, while the Owner group names the linked contractor record.
 * Empty fields remain visible so a missing value does not look like an absent field.
 */
export const MyDriverQuickView = ({
    driver,
    assignments,
    assignmentsFailed,
    onRetryAssignments,
    companyDeposit,
    companyDepositLoading = false,
    companyDepositError,
}: {
    driver: BoxTruckVerification
    assignments?: BoxTruckDriverAssignmentsDto | null
    /** The assignments read was refused or never answered — NOT "this driver has nothing attached". */
    assignmentsFailed?: boolean
    onRetryAssignments?: () => void
    /** Only the My Drivers page supplies this; Verifications never requests financial details. */
    companyDeposit?: BoxTruckMyDriverCompanyDepositDto
    companyDepositLoading?: boolean
    companyDepositError?: string
}) => {
    const [query, setQuery] = useState('')
    const [group, setGroup] = useState<string | null>(null)
    // Once per open, not per keystroke: the question is whether the search is reached for at all, and a
    // per-character event would answer it by flooding the axis with the same answer.
    const searchReported = useRef(false)
    const onQueryChange = (next: string) => {
        if (next && !searchReported.current) {
            searchReported.current = true
            trackBoxTruckUiInteraction('driver_field_searched')
        }
        setQuery(next)
    }
    const info = driver.boxTruckInfo.info
    const source = driver.sourceProfile
    const vehicle = assignments?.vehicle
    const assignedOwner = assignments?.owner
    const hasCompanyDepositRead = companyDepositLoading || Boolean(companyDeposit) || Boolean(companyDepositError)

    const groups = useMemo(
        () => [
            {
                title: 'Driver',
                fields: [
                    { label: 'Name', value: info?.name || driver.name },
                    { label: 'Phone', value: info?.phone },
                    { label: 'Email', value: driver.email },
                    // A name and a number, not two tiles: neither is any use without the other, and the
                    // panel is read in the one situation where reading it twice costs something.
                    { label: 'Emergency contact', value: join(' · ', info?.emergencyContact, info?.emergencyContactPhone) },
                    // Last location and HR Manager are in the page HEADER, not here — see `DriverHeaderFacts`.
                    // They are what somebody opens this screen already looking for, and the location needs to
                    // be correctable from the same place it is read.
                ],
            },
            {
                title: 'Vehicle',
                fields: [
                    { label: 'Equipment', value: info?.equipmentType },
                    { label: 'Make / model / year', value: join(' ', info?.vehicleMake, info?.vehicleModel, info?.vehicleYear) },
                    { label: 'VIN', value: info?.vinNumber },
                    { label: 'Truck #', value: info?.truckNumber },
                    { label: 'Payload (lbs)', value: info?.maxCargoWeightLbs },
                    { label: 'Cargo (L×W×H)', value: formatBoxTruckDimensions(info?.cargoDimensions) },
                    { label: 'Door (W×H)', value: formatBoxTruckDoor(info?.doorWidth, info?.doorHeight) },
                    { label: 'Insurance expires', value: info?.insuranceExpirationDate },
                    // The Assets record this driver is seated on — one line naming the asset whose documents
                    // are offered below, not a second vehicle beside the first. Nothing operational reads it:
                    // dispatch, the Telegram search and both lists read the claim's own `boxTruck.*`, which is
                    // the rest of this group and what the registration form edits.
                    { label: 'Assigned asset', value: vehicle?.unitNumber || vehicle?.vin },
                ],
            },
            {
                title: hasCompanyDepositRead ? 'License' : 'License & Carrier',
                fields: [
                    { label: 'License #', value: info?.licenseNumber },
                    // State and type answer one question about one licence, so they share its tile.
                    { label: 'License class', value: join(' · ', info?.licenseState, info?.licenseType) },
                    // The date is the answer; a separate `expiredLicense` flag only restates it, and the two
                    // disagree the moment one of them goes stale.
                    { label: 'License expires', value: info?.licenseExpirationDate },
                    { label: 'Medical card expires', value: source?.medicalCardExpirationDate },
                    ...(hasCompanyDepositRead
                        ? []
                        : [
                              { label: 'USDOT', value: info?.dotNumber },
                              { label: 'MC', value: info?.mcNumber },
                          ]),
                ],
            },
            {
                title: 'Owner',
                fields: [
                    { label: 'Company', value: assignedOwner?.companyName || assignedOwner?.name || assignedOwner?.doingBusinessAs },
                    { label: 'Applicant', value: assignedOwner?.applicantName },
                    { label: 'Owner phone', value: assignedOwner?.phone },
                    { label: 'Owner email', value: assignedOwner?.email },
                    { label: 'Address', value: assignedOwner?.address },
                    // MC and USDOT are NOT repeated here. The operator enters them on the driver
                    // (`REGISTRATION_FIELD_PATHS` writes `driverCompany.*`), so the copy in License & Carrier
                    // is the one HR filled in, and two fields reading "MC" is the ambiguity this panel keeps
                    // removing.
                ],
            },
            ...(companyDeposit ? myDriverCompanyDepositGroups(companyDeposit) : []),
        ],
        [driver, info, source, vehicle, assignedOwner, companyDeposit, hasCompanyDepositRead]
    )

    // Two narrowings, and they compose. The GROUP is for when the operator knows the neighbourhood but not
    // the field name ("it's something about the licence"); the NEEDLE matches the label or the value, so both
    // "vin" and a half-remembered plate find the same tile. A group with nothing left drops out while
    // filtering — the only time a field disappears from this panel.
    const needle = query.trim().toLowerCase()
    const shown = useMemo(() => {
        const inGroup = group ? groups.filter((candidate) => candidate.title === group) : groups
        if (!needle) return inGroup
        return inGroup
            .map((candidate) => ({
                ...candidate,
                fields: candidate.fields.filter(
                    (field) =>
                        field.label.toLowerCase().includes(needle) ||
                        String(field.value ?? '')
                            .toLowerCase()
                            .includes(needle)
                ),
            }))
            .filter((candidate) => candidate.fields.length > 0)
    }, [groups, needle, group])

    const count = (list: { fields: QuickViewField[] }[]) => list.reduce((sum, candidate) => sum + candidate.fields.length, 0)
    const total = count(group ? groups.filter((candidate) => candidate.title === group) : groups)
    const matching = count(shown)
    const firstFinancialGroup = shown.findIndex((candidate) => candidate.title === 'Company Details' || candidate.title === 'Direct Deposit')

    return (
        <div className="space-y-5 rounded-2xl bg-surface p-5 shadow-surface">
            {/* Sticky, because finding a field is what the search is for and it is no use once it has
                scrolled away. It bleeds to the card's edges so the tiles pass behind it rather than beside it. */}
            {/* The search, the groups and the count are ONE row: they are three halves of a single act —
                narrow the panel down to the field you want — and stacking them gave a full-width search bar
                a line of its own above a line the eye had to travel back to. The search takes the space that
                is left, the groups and the count take what they need, and on a narrow panel the groups wrap
                below rather than squeezing the input to nothing. */}
            <div className="sticky -top-5 z-10 -mx-5 -mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 bg-surface px-5 pb-3 pt-5">
                <div className="min-w-[200px] flex-1">
                    <SearchField value={query} onChange={onQueryChange} onClear={() => setQuery('')} placeholder="Find a field — VIN, MC, phone…" />
                </div>
                {/* Selecting the active group clears it, which is the "All" affordance without a button that
                    is selected by default and therefore says nothing. */}
                <ToggleButtonGroup
                    size="sm"
                    items={groups.map((candidate) => ({ key: candidate.title, label: candidate.title }))}
                    selectedKeys={group ? new Set([group]) : new Set()}
                    onSelectionChange={(keys) => {
                        const next = [...keys][0] ?? null
                        // The group TITLE, which is a closed set of four defined in this file — never a value
                        // off a driver record.
                        if (next) trackBoxTruckUiInteraction('driver_group_filtered', next)
                        setGroup(next)
                    }}
                />
                <span className="shrink-0 text-xs tabular-nums text-foreground-muted">{needle ? `${matching} of ${total}` : `${total} fields`}</span>
            </div>
            {assignmentsFailed && (
                // Without this the two Assigned groups are a column of dashes, which reads as "this driver
                // has no owner and no vehicle" — a statement about the driver that nobody has verified.
                <Alert
                    variant="danger"
                    title="The assigned vehicle and owner could not be loaded"
                    description="Those two groups are blank for that reason, not because the driver has neither."
                    actionLabel={onRetryAssignments ? 'Try again' : undefined}
                    onAction={onRetryAssignments}
                />
            )}
            {companyDepositLoading && (
                <p role="status" className="text-sm text-foreground-muted">
                    Loading company and direct deposit details…
                </p>
            )}
            {companyDepositError && <Alert variant="danger" title="Company and direct deposit details could not be loaded" description={companyDepositError} />}
            {shown.length === 0 ? (
                <div className="py-6 text-center text-sm text-foreground-muted">
                    No field matches “{query}”{group ? ` in ${group}` : ''}.
                </div>
            ) : (
                shown.map((candidate, index) => (
                    <Fragment key={candidate.title}>
                        {index === firstFinancialGroup && (
                            <>
                                <div>
                                    <h2 className="text-base font-semibold text-foreground">Company and Direct Deposit</h2>
                                    <p className="text-xs text-foreground-muted">
                                        {companyDeposit?.source === 'owner' ? 'Using this owner’s current details' : 'Using this driver’s details'}
                                    </p>
                                </div>
                                {companyDeposit?.ownerDetailsMissing && (
                                    <p role="status" className="text-sm text-warning">
                                        The linked owner’s details are unavailable. Check the owner assignment in Profiles.
                                    </p>
                                )}
                            </>
                        )}
                        <Group title={candidate.title} fields={candidate.fields} showTitle={!group} />
                    </Fragment>
                ))
            )}
        </div>
    )
}
